import { type ReactNode, Children } from "react";
import { Link } from "@tanstack/react-router";
import { format, parseISO } from "date-fns";
import { Plus, ShoppingCart } from "lucide-react";
import { ErrorBoundary } from "@/components/shared/error-boundary";
import {
  MasterBanner,
  MasterHeader,
  MasterPage,
  MasterSummaryStrip,
} from "@/components/shared/master-shell";
import { StatusDot } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { formatMoney } from "@/features/clients/constants";
import { useDashboard } from "@/features/dashboard/api";
import { fmtQty } from "@/features/inventory/constants";
import { useAuth } from "@/lib/auth";
import { isSupabaseConfigured } from "@/lib/supabase";

export function DashboardPage() {
  return (
    <ErrorBoundary title="Dashboard crashed">
      <DashboardInner />
    </ErrorBoundary>
  );
}

function DashboardInner() {
  const { profile, user } = useAuth();
  const { data, isLoading, error, refetch, isFetching } = useDashboard();
  const name = profile?.fname ?? user?.email?.split("@")[0] ?? "there";
  const k = data?.kpis;
  const today = format(new Date(), "yyyy-MM-dd");

  return (
    <MasterPage>
      <MasterHeader
        title={`Operations · ${name}`}
        countLabel={isLoading ? "Loading…" : "Live plant & commercial snapshot"}
        onRefresh={() => void refetch()}
        refreshing={isFetching}
        actions={
          <>
            <Button asChild size="sm" variant="outline">
              <Link to="/purchase/orders/new">
                <Plus />
                PO
              </Link>
            </Button>
            <Button asChild size="sm">
              <Link to="/sales-orders/new">
                <ShoppingCart />
                SO
              </Link>
            </Button>
          </>
        }
      />

      {!isSupabaseConfigured ? (
        <MasterBanner>Connect Supabase to load live KPIs.</MasterBanner>
      ) : null}

      {error ? (
        <MasterBanner tone="danger">{(error as Error).message}</MasterBanner>
      ) : null}

      <MasterSummaryStrip
        items={[
          {
            label: "Stock wt",
            value: isLoading ? "…" : fmtQty(k?.stockWeight ?? 0),
          },
          {
            label: "Open POs",
            value: isLoading ? "…" : String(k?.openPoCount ?? 0),
          },
          {
            label: "PO value",
            value: isLoading ? "…" : formatMoney(k?.openPoValue ?? 0),
          },
          {
            label: "Open SOs",
            value: isLoading ? "…" : String(k?.openSoCount ?? 0),
          },
          {
            label: "SO value",
            value: isLoading ? "…" : formatMoney(k?.openSoValue ?? 0),
          },
          {
            label: "Overdue AR",
            value: isLoading ? "…" : formatMoney(k?.overdueAr ?? 0),
            tone: (k?.overdueAr ?? 0) > 0 ? "warning" : "default",
          },
          {
            label: "Outstanding",
            value: isLoading ? "…" : formatMoney(k?.outstandingAr ?? 0),
          },
          {
            label: "QC open",
            value: isLoading ? "…" : String(k?.qcHoldCount ?? 0),
            tone: (k?.qcHoldCount ?? 0) > 0 ? "warning" : "default",
          },
          {
            label: "Ready stages",
            value: isLoading ? "…" : String(k?.readyStages ?? 0),
          },
          {
            label: "Dispatch open",
            value: isLoading ? "…" : String(k?.dispatchOpen ?? 0),
          },
        ]}
      />

      {(k?.partialPoCount ?? 0) > 0 ? (
        <MasterBanner>
          {k!.partialPoCount} partial PO(s) still receiving —{" "}
          <Link to="/purchase" className="text-primary hover:underline">
            Purchase
          </Link>
        </MasterBanner>
      ) : null}

      <div className="grid gap-3 lg:grid-cols-3">
        <ActionList
          title="Urgent sales orders"
          href="/sales-orders"
          empty="No open SOs due within 7 days"
          loading={isLoading}
        >
          {(data?.urgentSos ?? []).map((row) => {
            const overdue = Boolean(row.due_date && row.due_date < today);
            return (
              <Link
                key={row.id}
                to="/sales-orders/$soId"
                params={{ soId: row.id }}
                className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs hover:bg-muted/40"
              >
                <span className="min-w-0 truncate">
                  <span className="font-mono font-medium">{row.so_number}</span>
                  <span className="ml-1.5 text-muted-foreground">{row.client_name || "—"}</span>
                </span>
                <span className="flex shrink-0 items-center gap-2">
                  <StatusDot tone={overdue ? "danger" : "warning"}>
                    {row.due_date ? format(parseISO(row.due_date), "dd MMM") : "—"}
                  </StatusDot>
                  <span className="tabular-nums text-muted-foreground">
                    {formatMoney(row.grand_total)}
                  </span>
                </span>
              </Link>
            );
          })}
        </ActionList>

        <ActionList
          title="Overdue invoices"
          href="/invoices"
          empty="No issued invoices past due"
          loading={isLoading}
        >
          {(data?.overdueInvoices ?? []).map((row) => (
            <Link
              key={row.id}
              to="/invoices/$invId"
              params={{ invId: row.id }}
              className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs hover:bg-muted/40"
            >
              <span className="min-w-0 truncate">
                <span className="font-mono font-medium">{row.invoice_number}</span>
                <span className="ml-1.5 text-muted-foreground">{row.client_name || "—"}</span>
              </span>
              <span className="flex shrink-0 items-center gap-2">
                <StatusDot tone="danger">
                  {format(parseISO(row.due_date), "dd MMM")}
                </StatusDot>
                <span className="tabular-nums text-muted-foreground">
                  {formatMoney(row.grand_total)}
                </span>
              </span>
            </Link>
          ))}
        </ActionList>

        <ActionList
          title="QC holds"
          href="/quality"
          empty="No in-stock batches on hold"
          loading={isLoading}
        >
          {(data?.qcHolds ?? []).map((row) => (
            <Link
              key={row.id}
              to="/inventory/$batchId"
              params={{ batchId: row.id }}
              className="flex items-center justify-between gap-2 px-2 py-1.5 text-xs hover:bg-muted/40"
            >
              <span className="min-w-0 truncate">
                <span className="font-mono font-medium">{row.batch_no}</span>
                <span className="ml-1.5 text-muted-foreground">{row.material_name || "—"}</span>
              </span>
              <span className="tabular-nums text-muted-foreground">{fmtQty(row.weight)}</span>
            </Link>
          ))}
        </ActionList>
      </div>
    </MasterPage>
  );
}

function ActionList({
  title,
  href,
  empty,
  loading,
  children,
}: {
  title: string;
  href: string;
  empty: string;
  loading?: boolean;
  children: ReactNode;
}) {
  const count = Children.toArray(children).length;

  return (
    <section className="overflow-hidden rounded-md border border-border bg-card">
      <div className="flex items-center justify-between border-b border-border px-2 py-1.5">
        <h2 className="text-xs font-semibold">{title}</h2>
        <Button asChild variant="ghost" size="xs">
          <Link to={href}>All</Link>
        </Button>
      </div>
      {loading ? (
        <p className="px-2 py-6 text-center text-2xs text-muted-foreground">Loading…</p>
      ) : count === 0 ? (
        <p className="px-2 py-6 text-center text-2xs text-muted-foreground">{empty}</p>
      ) : (
        <ul className="divide-y divide-border/70">{children}</ul>
      )}
    </section>
  );
}
